import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { UserMinus, Plus, Search, RotateCcw, Loader2, Lock, FileDown } from "lucide-react";

const REASONS = ["Cambio de escuela", "Económico", "Mudanza", "Conducta", "Otro"];
const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;
const inp = "w-full border border-nardo-200 rounded-md px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";
const errMsg = (e, d) => e?.response?.data?.detail || d;

function BajaFields({ f, set }) {
  return (
    <div className="space-y-3">
      <label className="block text-xs font-semibold text-nardo-600">Motivo
        <select data-testid="baja-reason" className={inp} value={f.reasonSel} onChange={(e) => set({ reasonSel: e.target.value })}>
          {REASONS.map((r) => <option key={r}>{r}</option>)}
        </select>
      </label>
      {f.reasonSel === "Otro" && <input data-testid="baja-reason-other" className={inp} placeholder="Especifica el motivo" value={f.other} onChange={(e) => set({ other: e.target.value })} />}
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-xs font-semibold text-nardo-600">Fecha de baja
          <input data-testid="baja-date" type="date" className={inp} value={f.date} onChange={(e) => set({ date: e.target.value })} />
        </label>
        <label className="block text-xs font-semibold text-nardo-600">Adeudo pendiente
          <input data-testid="baja-debt" type="number" min="0" step="0.01" className={inp} value={f.debt} onChange={(e) => set({ debt: e.target.value })} />
        </label>
      </div>
      <label className="block text-xs font-semibold text-nardo-600">Comentarios u observaciones
        <textarea data-testid="baja-comments" rows={3} className={inp} value={f.comments} onChange={(e) => set({ comments: e.target.value })} />
      </label>
    </div>
  );
}

const payloadOf = (f) => ({ reason: f.reasonSel === "Otro" ? (f.other.trim() || "Otro") : f.reasonSel, date: f.date, comments: f.comments, debt: Number(f.debt) || 0, definitive: !!f.definitive });
const emptyForm = () => ({ reasonSel: REASONS[0], other: "", date: today(), debt: "", comments: "", definitive: false });

export function BajaDialog({ student, onClose, onDone }) {
  const [f, setF] = useState(emptyForm());
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  useEffect(() => {
    if (!student) return;
    setF(emptyForm());
    api.get(`/students/${student.id}/statement`).then(({ data }) => set({ debt: data?.balance ? String(data.balance) : "0" })).catch(() => {});
  }, [student]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/students/${student.id}/baja`, payloadOf(f));
      toast.success("Alumno dado de baja. Quedó registrado en el histórico.");
      onDone?.(); onClose();
    } catch (e) { toast.error(errMsg(e, "No se pudo dar de baja")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="baja-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Dar de baja a {student?.full_name}</DialogTitle></DialogHeader>
        <p className="text-xs text-nardo-500">El alumno sale de las listas activas (asistencia, cobranza y grupos), pero su expediente se conserva para siempre en el Histórico de bajas.</p>
        <BajaFields f={f} set={set} />
        <label className="flex items-center gap-2 text-sm text-nardo-700">
          <input data-testid="baja-definitive" type="checkbox" checked={f.definitive} onChange={(e) => set({ definitive: e.target.checked })} />
          Baja definitiva (no permite reingreso)
        </label>
        <button data-testid="baja-submit" disabled={busy} onClick={submit} className="w-full bg-red-600 hover:bg-red-700 text-white rounded-md py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />} Confirmar baja
        </button>
      </DialogContent>
    </Dialog>
  );
}

function ManualDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ ...emptyForm(), full_name: "", grade: "", level: "", tutor_name: "", tutor_phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const submit = async () => {
    if (!f.full_name.trim()) { toast.error("Escribe el nombre del alumno"); return; }
    setBusy(true);
    try {
      await api.post("/bajas/manual", { ...payloadOf(f), full_name: f.full_name, grade: f.grade, level: f.level, tutor_name: f.tutor_name, tutor_phone: f.tutor_phone });
      toast.success("Baja registrada en el histórico");
      setF({ ...emptyForm(), full_name: "", grade: "", level: "", tutor_name: "", tutor_phone: "" });
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e, "No se pudo registrar")); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="baja-manual-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Registrar baja anterior</DialogTitle></DialogHeader>
        <p className="text-xs text-nardo-500">Para alumnos que se dieron de baja antes y no quedó registro.</p>
        <input data-testid="baja-manual-name" className={inp} placeholder="Nombre completo del alumno" value={f.full_name} onChange={(e) => set({ full_name: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="baja-manual-grade" className={inp} placeholder="Grado y grupo (ej. 3A)" value={f.grade} onChange={(e) => set({ grade: e.target.value })} />
          <select data-testid="baja-manual-level" className={inp} value={f.level} onChange={(e) => set({ level: e.target.value })}>
            <option value="">Nivel</option>{["Preescolar", "Primaria", "Secundaria", "Preparatoria"].map((l) => <option key={l}>{l}</option>)}
          </select>
          <input data-testid="baja-manual-tutor" className={inp} placeholder="Tutor (opcional)" value={f.tutor_name} onChange={(e) => set({ tutor_name: e.target.value })} />
          <input data-testid="baja-manual-phone" className={inp} placeholder="Teléfono (opcional)" value={f.tutor_phone} onChange={(e) => set({ tutor_phone: e.target.value })} />
        </div>
        <BajaFields f={f} set={set} />
        <button data-testid="baja-manual-submit" disabled={busy} onClick={submit} className="w-full bg-navy text-white rounded-md py-2.5 text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Guardar en el histórico
        </button>
      </DialogContent>
    </Dialog>
  );
}

const STATUS = {
  baja: ["Baja", "bg-red-50 text-red-700"],
  reingresado: ["Reingresado", "bg-emerald-50 text-emerald-700"],
  eliminado: ["Eliminado", "bg-nardo-100 text-nardo-700"],
};
const fmtDate = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function BajaRow({ b, onReingreso }) {
  const [label, cls] = STATUS[b.status] || STATUS.baja;
  const tutor = (b.tutors || [])[0];
  const canReturn = b.status === "baja" && !b.definitive && b.student_id;
  return (
    <tr className="border-t border-nardo-100 align-top" data-testid={`baja-row-${b.id}`}>
      <td className="px-3 py-2.5"><div className="font-semibold text-navy">{b.full_name}</div><div className="text-xs text-nardo-500">{[b.grade, b.level].filter(Boolean).join(" · ") || "—"}{tutor?.name ? ` · Tutor: ${tutor.name}` : ""}</div></td>
      <td className="px-3 py-2.5 whitespace-nowrap">{fmtDate(b.date)}</td>
      <td className="px-3 py-2.5"><div>{b.reason}</div>{b.comments && <div className="text-xs text-nardo-500 max-w-xs">{b.comments}</div>}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{money(b.debt)}</td>
      <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>{b.definitive && b.status === "baja" && <Lock className="inline w-3 h-3 ml-1 text-nardo-400" />}{b.source === "manual" && <div className="text-[10px] text-nardo-400 mt-1">Captura manual</div>}</td>
      <td className="px-3 py-2.5 text-right">{canReturn && <button data-testid={`baja-reingreso-${b.id}`} onClick={() => onReingreso(b)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded-md"><RotateCcw className="w-3.5 h-3.5" /> Reingresar</button>}</td>
    </tr>
  );
}

const exportCsv = (rows) => {
  const head = ["Alumno", "Grado", "Nivel", "Fecha de baja", "Motivo", "Comentarios", "Adeudo", "Estado", "Registró"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rows.map((b) => [b.full_name, b.grade, b.level, b.date, b.reason, b.comments, b.debt, (STATUS[b.status] || STATUS.baja)[0], b.created_by].map(esc).join(","));
  const blob = new Blob(["\ufeff" + [head.join(","), ...body].join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `historico_bajas_${today()}.csv`; a.click();
};

export default function HistorialBajas() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [st, setSt] = useState("");
  const [manual, setManual] = useState(false);
  const load = () => api.get("/bajas").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => (items || []).filter((b) => (!st || b.status === st) && (!q || `${b.full_name} ${b.reason} ${b.grade}`.toLowerCase().includes(q.toLowerCase()))), [items, q, st]);
  const debtTotal = rows.reduce((s, b) => s + Number(b.status === "baja" ? b.debt || 0 : 0), 0);
  const onReingreso = async (b) => {
    if (!window.confirm(`¿Reingresar a ${b.full_name}? Volverá a las listas activas.`)) return;
    try { const { data } = await api.post(`/bajas/${b.id}/reingreso`); toast.success(data.nfc_restored ? "Alumno reingresado (tarjeta NFC restaurada)" : "Alumno reingresado"); load(); }
    catch (e) { toast.error(errMsg(e, "No se pudo reingresar")); }
  };
  return (
    <div className="space-y-4" data-testid="historial-bajas">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="font-display font-bold text-2xl text-navy">Histórico de bajas</h2><p className="text-sm text-nardo-500">Registro permanente de alumnos dados de baja.</p></div>
        <div className="flex gap-2">
          <button data-testid="bajas-export" onClick={() => exportCsv(rows)} className="inline-flex items-center gap-2 border border-nardo-200 px-3 py-2 rounded-md text-sm text-navy hover:bg-nardo-50"><FileDown className="w-4 h-4" /> Exportar</button>
          <button data-testid="bajas-add-manual" onClick={() => setManual(true)} className="inline-flex items-center gap-2 bg-navy text-white px-3 py-2 rounded-md text-sm font-semibold"><Plus className="w-4 h-4" /> Registrar baja anterior</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {[["Total", (items || []).length, "bajas-count-total"], ["Bajas activas", (items || []).filter((b) => b.status === "baja").length, "bajas-count-active"], ["Adeudo (filtro)", money(debtTotal), "bajas-debt-total"]].map(([l, v, t]) => (
          <div key={t} className="bg-white border border-nardo-200 rounded-md p-3"><div className="text-xs text-nardo-500">{l}</div><div data-testid={t} className="font-display font-extrabold text-xl text-navy tabular-nums">{v}</div></div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-nardo-400" /><input data-testid="bajas-search" className={`${inp} pl-9`} placeholder="Buscar por nombre, motivo o grado" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select data-testid="bajas-filter-status" className={`${inp} !w-48`} value={st} onChange={(e) => setSt(e.target.value)}>
          <option value="">Todos</option><option value="baja">Baja</option><option value="reingresado">Reingresados</option><option value="eliminado">Eliminados</option>
        </select>
      </div>
      <div className="bg-white border border-nardo-200 rounded-md overflow-x-auto">
        {items === null ? <div className="py-12 text-center text-sm text-nardo-500">Cargando…</div> : rows.length === 0 ? <div className="py-12 text-center text-sm text-nardo-500" data-testid="bajas-empty">No hay bajas registradas.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-nardo-50 text-xs text-nardo-600 text-left"><tr><th className="px-3 py-2">Alumno</th><th className="px-3 py-2">Fecha</th><th className="px-3 py-2">Motivo</th><th className="px-3 py-2 text-right">Adeudo</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2" /></tr></thead>
            <tbody>{rows.map((b) => <BajaRow key={b.id} b={b} onReingreso={onReingreso} />)}</tbody>
          </table>
        )}
      </div>
      <ManualDialog open={manual} onClose={() => setManual(false)} onDone={load} />
    </div>
  );
}
